// 매일 강의 관제 — 관리자만.
//
// 봇 다섯이 매일 차시 하나씩 채우고 다듬는다. 사람이 여기서 보는 것은 셋이다.
//
//   누가 무엇을 맡았는가   담당이 없으면 그 봇은 오늘 몫을 못 받는다
//   어디까지 왔는가        차시마다 몇 회차이고 자료가 몇 건 깔렸는가
//   무엇이 배정됐는가      최근 일주일
//
// 담당을 여기서 바꾸면 decided_by 가 human 이 되어 봇이 되돌리지 못한다.
// 사람이 정한 것을 봇이 밤사이 바꿔 놓으면 아침에 영문을 모른다.

import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import useLMSAuth from '../hooks/useLMSAuth';

const Page = styled.div`
  min-height: 100vh; background: #f8fafc; padding: 28px 20px 60px;
`;
const Inner = styled.div`max-width: 1120px; margin: 0 auto;`;
const Head = styled.header`
  display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; margin-bottom: 6px;
  h1 { font-size: 1.5rem; font-weight: 800; margin: 0; color: #0f172a; }
  span { font-size: 0.85rem; color: #64748b; }
`;
const Lead = styled.p`margin: 0 0 22px; font-size: 0.9rem; color: #64748b; line-height: 1.7;`;

const Card = styled.section`
  background: #fff; border: 1px solid #e2e8f0; border-radius: 14px;
  padding: 18px 20px; margin-bottom: 18px;
`;
const CardTitle = styled.h2`
  font-size: 1rem; font-weight: 800; margin: 0 0 4px; color: #0f172a;
`;
const CardNote = styled.p`margin: 0 0 14px; font-size: 0.8rem; color: #64748b; line-height: 1.6;`;

const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: 0.86rem;
  th, td { padding: 9px 8px; text-align: left; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
  th { font-size: 0.76rem; color: #64748b; font-weight: 700; }
  tbody tr:last-child td { border-bottom: 0; }
`;
const Select = styled.select`
  padding: 6px 8px; border: 1px solid #cbd5e1; border-radius: 7px;
  font-size: 0.85rem; background: #fff; min-width: 210px;
`;
const Tag = styled.span`
  display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 0.72rem; font-weight: 700;
  background: ${({ $tone }) => ($tone === 'human' ? '#eff6ff' : $tone === 'none' ? '#fef2f2' : '#f0fdf4')};
  color: ${({ $tone }) => ($tone === 'human' ? '#1d4ed8' : $tone === 'none' ? '#b91c1c' : '#15803d')};
`;
const Btn = styled.button`
  padding: 5px 10px; border-radius: 7px; cursor: pointer; font-size: 0.78rem; font-weight: 700;
  white-space: nowrap;
  border: 1px solid #cbd5e1; background: #fff; color: #334155;
  &:hover { border-color: #0f766e; color: #0f766e; }
  &:disabled { opacity: 0.5; cursor: default; }
`;
const Msg = styled.div`
  margin-bottom: 14px; padding: 10px 12px; border-radius: 9px; font-size: 0.85rem;
  background: ${({ $bad }) => ($bad ? '#fef2f2' : '#f0fdf4')};
  color: ${({ $bad }) => ($bad ? '#b91c1c' : '#15803d')};
`;

// 한눈에 보는 숫자.
//
// 표를 세어 보게 하면 "왜 아무것도 안 올라왔지" 에 답하는 데 시간이 걸린다.
// 막힌 곳은 빨갛게 둔다 — 0 이면 그 줄에서 멈춰 있다는 뜻이다.
const Stats = styled.div`
  display: grid; gap: 10px; margin-bottom: 18px;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
`;
const Stat = styled.div`
  background: #fff; border-radius: 12px; padding: 14px 16px;
  border: 1px solid ${({ $bad }) => ($bad ? '#fecaca' : '#e2e8f0')};
  .label { font-size: 0.74rem; color: #64748b; font-weight: 700; }
  .value {
    font-size: 1.5rem; font-weight: 800; margin-top: 4px;
    font-variant-numeric: tabular-nums;
    color: ${({ $bad }) => ($bad ? '#b91c1c' : '#0f172a')};
  }
  .sub { font-size: 0.72rem; color: #94a3b8; margin-top: 2px; }
`;

const Bar = styled.div`
  height: 7px; border-radius: 99px; background: #e2e8f0; overflow: hidden; min-width: 90px;
  div { height: 100%; background: linear-gradient(90deg, #0d9488, #14b8a6); }
`;
const Rev = styled.span`
  display: inline-block; min-width: 34px; font-variant-numeric: tabular-nums;
  color: ${({ $done }) => ($done ? '#15803d' : '#94a3b8')}; font-weight: 700;
`;

export default function DailyLectureAdminPage() {
  const { login, role, loading } = useLMSAuth();
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await fetch('/lms/admin/daily-lectures', { credentials: 'include' });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setMsg({ bad: true, text: d.error || '불러오지 못했습니다.' }); return; }
    setData(d);
  }, []);

  useEffect(() => { if (!loading && role === 'admin') load(); }, [loading, role, load]);

  if (loading) return <Page><Inner>불러오는 중…</Inner></Page>;
  if (!login) return <Page><Inner>로그인이 필요합니다.</Inner></Page>;
  if (role !== 'admin') {
    return <Page><Inner>관리자만 볼 수 있습니다. (현재 권한: {role})</Inner></Page>;
  }
  if (!data) return <Page><Inner>{msg ? msg.text : '불러오는 중…'}</Inner></Page>;

  const { agents, courses, recentAssignments, targetRevisions } = data;
  // 서버가 요약을 안 주는 옛 응답에서도 화면이 서 있어야 한다.
  const s = data.summary || {
    agents: agents.length, routineOn: 0, withDomain: 0, courses: courses.length,
    lessons: 0, written: 0, completed: 0, research: 0, simulatorsPending: 0, assignedToday: 0,
  };
  // 이미 남이 맡은 과정은 고를 수 없다. 지금 그 봇이 맡은 것은 남겨 둔다.
  const takenBy = new Map(courses.filter(c=>c.owner_agent_id).map(c=>[c.id,c.owner_agent_id]));

  const setDomain = async (agentId, courseId) => {
    setBusy(true); setMsg(null);
    try {
      const r = await fetch(`/lms/agent-domains/${agentId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ course_id: courseId === '' ? null : Number(courseId), rationale: '관리자 지정' }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || '바꾸지 못했습니다.');
      setMsg({ text: courseId === '' ? '담당을 해제했습니다.' : '담당을 정했습니다.' });
      await load();
    } catch (e) {
      setMsg({ bad: true, text: e.message });
    } finally { setBusy(false); }
  };

  const setSecondary = async (agent, courseId, checked) => {
    const current=(agent.courses||[]).filter(c=>!c.is_primary).map(c=>c.course_id);
    const secondary=checked?[...current,courseId]:current.filter(id=>id!==courseId);
    setBusy(true);setMsg(null);
    try {
      const r=await fetch(`/lms/agent-domains/${agent.agent_id}`,{method:'PUT',credentials:'include',
        headers:{'Content-Type':'application/json'},body:JSON.stringify({secondary_course_ids:secondary,rationale:'관리자 보조 담당 지정'})});
      const result=await r.json();if(!r.ok)throw new Error(result.error||'보조 담당을 저장하지 못했습니다.');
      await load();setMsg({text:'주 담당을 유지하고 보조 담당을 저장했습니다.'});
    }catch(error){setMsg({bad:true,text:error.message});}finally{setBusy(false);}
  };

  const toggleSeal = async (lessonId, sealed) => {
    setBusy(true); setMsg(null);
    try {
      const r = await fetch(`/lms/lessons/${lessonId}/seal`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sealed: !sealed }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || '바꾸지 못했습니다.');
      await load();
    } catch (e) {
      setMsg({ bad: true, text: e.message });
    } finally { setBusy(false); }
  };

  const undecided = agents.filter((a) => !(a.courses?.length||a.course_id)).length;
  // 담당은 있는데 정기 업무가 꺼진 봇. 이 상태가 제일 헷갈린다 —
  // 다 맞춰 놓은 것 같은데 아침에 아무것도 안 올라온다.
  const offDuty = agents.filter((a) => (a.courses?.length||a.course_id) && !a.routine_enabled).length;

  return (
    <Page>
      <Inner>
        <Head>
          <h1>매일 강의 관제</h1>
          <span>봇 {agents.length}마리 · 과정 {courses.length}개 · 초기 검토 {targetRevisions}회차 후에도 매일 개선</span>
        </Head>
        <Lead>
          봇이 매일 주 담당과 보조 담당 과정의 차시를 순환하며 자료와 실습을 개선합니다.
          담당이 없는 봇은 오늘 몫을 받지 못합니다.
        </Lead>

        {/* 위에서부터 순서대로 막힌다. 정기업무가 꺼져 있으면 담당이 있어도
            안 돌고, 담당이 없으면 배정이 안 나오고, 배정이 없으면 자료가
            안 쌓인다. 그 순서대로 늘어놓는다. */}
        <Stats data-testid="summary">
          <Stat $bad={s.routineOn === 0}>
            <div className="label">정기 업무 켜짐</div>
            <div className="value">{s.routineOn} / {s.agents}</div>
            <div className="sub">{s.routineOn === 0 ? '에이전트 관리에서 켜세요' : '봇'}</div>
          </Stat>
          <Stat $bad={s.withDomain === 0}>
            <div className="label">담당 정해짐</div>
            <div className="value">{s.withDomain} / {s.agents}</div>
            <div className="sub">{s.unassignedCourses ? `과정 ${s.unassignedCourses}개가 비어 있음` : `전체 ${s.assignedCourses ?? s.withDomain}개 과정 담당 배정`}</div>
          </Stat>
          <Stat>
            <div className="label">오늘 배정</div>
            <div className="value">{s.assignedToday}</div>
            <div className="sub">건</div>
          </Stat>
          <Stat $bad={s.written === 0}>
            <div className="label">작성된 차시</div>
            <div className="value">{s.written} / {s.lessons}</div>
            <div className="sub">3회 검토 {s.completed}개 · 관리자 확정 {s.sealed || 0}개</div>
          </Stat>
          <Stat>
            <div className="label">쌓인 조사 자료</div>
            <div className="value">{s.research}</div>
            <div className="sub">건</div>
          </Stat>
          <Stat>
            <div className="label">3D 준비 중</div>
            <div className="value">{s.simulatorsPending} / {s.courses}</div>
            <div className="sub">과정 · 그동안은 글로 실습</div>
          </Stat>
        </Stats>

        {msg && <Msg $bad={msg.bad}>{msg.text}</Msg>}
        {undecided > 0 && (
          <Msg $bad>
            담당이 없는 봇이 {undecided}마리 있습니다. 여기서 정하거나, 봇이 스스로 고르게 두세요.
          </Msg>
        )}
        {offDuty > 0 && (
          <Msg $bad>
            정기 업무가 꺼진 봇이 {offDuty}마리 있습니다 — 담당이 있어도 돌지 않습니다.
            앤츠톡 <b>에이전트 관리</b>에서 켜세요.
          </Msg>
        )}

        <Card>
          <CardTitle>봇 담당</CardTitle>
          <CardNote>
            한 봇이 여러 과정을 담당할 수 있습니다. 주 담당을 유지하고 아래 체크 항목으로 보조 담당을 추가하세요. 한 과정은 한 봇만 맡습니다. 관리자 확정으로 봉인하기 전까지 매일 보완합니다.
          </CardNote>
          <Table>
            <thead>
              <tr>
                <th style={{ width: 170 }}>봇</th>
                <th>담당 과정</th>
                <th style={{ width: 90 }}>정한 주체</th>
                <th style={{ width: 120 }}>정기 업무</th>
                <th>이유</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <tr key={a.agent_id}>
                  <td style={{ fontWeight: 700 }}>{a.agent_name}</td>
                  <td>
                    <Select
                      value={a.course_id || ''}
                      disabled={busy}
                      onChange={(e) => setDomain(a.agent_id, e.target.value)}
                    >
                      <option value="">— 담당 없음 —</option>
                      {courses.map((c) => (
                        <option
                          key={c.id}
                          value={c.id}
                          disabled={takenBy.has(c.id) && takenBy.get(c.id) !== a.agent_id}
                        >
                          {c.title}
                          {takenBy.has(c.id) && takenBy.get(c.id) !== a.agent_id ? ' (다른 봇 담당)' : ''}
                        </option>
                      ))}
                    </Select>
                    <div style={{display:'grid',gap:6,marginTop:10}} aria-label={`${a.agent_name} 보조 담당`}>
                      {courses.filter(c=>c.id!==a.course_id).map(c=><label key={c.id} style={{fontSize:12,display:'flex',gap:6,alignItems:'center'}}>
                        <input type="checkbox" aria-label={`${a.agent_name} 보조 담당 ${c.title}`}
                          checked={(a.courses||[]).some(x=>x.course_id===c.id&&!x.is_primary)}
                          disabled={busy||(takenBy.has(c.id)&&takenBy.get(c.id)!==a.agent_id)}
                          onChange={e=>setSecondary(a,c.id,e.target.checked)}/>
                        {c.title}{takenBy.has(c.id)&&takenBy.get(c.id)!==a.agent_id?' (다른 봇 담당)':''}
                      </label>)}
                    </div>
                  </td>
                  <td>
                    {a.course_id
                      ? <Tag $tone={a.decided_by === 'human' ? 'human' : 'self'}>
                          {a.decided_by === 'human' ? '사람' : '본인'}
                        </Tag>
                      : <Tag $tone="none">미정</Tag>}
                  </td>
                  <td>
                    {/* 켜고 끄는 곳은 앤츠톡 에이전트 관리다. 여기서는 상태만 본다. */}
                    {a.routine_enabled
                      ? (
                        <span title="앤츠톡 에이전트 관리에서 끕니다">
                          <Tag $tone="self">켜짐 {a.run_hour}시</Tag>
                          {a.last_run_date && (
                            <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 2 }}>
                              마지막 {String(a.last_run_date).slice(0, 10)}
                            </div>
                          )}
                        </span>
                      )
                      : <Tag $tone="none" title="앤츠톡 에이전트 관리에서 켭니다">꺼짐</Tag>}
                  </td>
                  <td style={{ color: '#64748b', fontSize: '0.8rem' }}>{a.rationale || '-'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>

        {courses.map((c) => {
          const done = c.lessons.filter((l) => l.revision >= targetRevisions || l.sealed_at).length;
          return (
            <Card key={c.id}>
              <CardTitle>
                {c.title}{' '}
                <span style={{ fontWeight: 500, fontSize: '0.82rem', color: '#64748b' }}>
                  {c.owner_agent_name ? `담당 ${c.owner_agent_name}` : '담당 없음'}
                </span>
              </CardTitle>
              <CardNote>
                {done}/{c.lessons.length} 차시 완성 · 시뮬레이터 {c.simulation_key}
              </CardNote>
              <Table>
                <thead>
                  <tr>
                    <th style={{ width: 150 }}>섹션</th>
                    <th>차시</th>
                    <th style={{ width: 70 }}>회차</th>
                    <th style={{ width: 110 }}>진행</th>
                    <th style={{ width: 60 }}>자료</th>
                    <th style={{ width: 60 }}>실습</th>
                    <th style={{ width: 130 }}>작성자</th>
                    <th style={{ width: 96 }} />
                  </tr>
                </thead>
                <tbody>
                  {c.lessons.map((l) => (
                    <tr key={l.id}>
                      <td style={{ color: '#94a3b8', fontSize: '0.78rem' }}>{l.section_title}</td>
                      <td>
                        <a href={`/Practice/${l.id}`} style={{ color: '#0f766e', textDecoration: 'none' }}>
                          {l.title}
                        </a>
                      </td>
                      <td>
                        <Rev $done={l.revision >= targetRevisions}>
                          {l.revision}/{targetRevisions}
                        </Rev>
                      </td>
                      <td>
                        <Bar>
                          <div style={{ width: `${Math.min(100, (l.revision / targetRevisions) * 100)}%` }} />
                        </Bar>
                      </td>
                      <td style={{ color: '#64748b' }}>{l.research}건</td>
                      <td>{l.has_practice ? '✔' : <span style={{ color: '#cbd5e1' }}>–</span>}</td>
                      <td style={{ color: '#64748b', fontSize: '0.78rem' }}>{l.author_name || '-'}</td>
                      <td>
                        {/* 회차가 남아도 사람이 됐다고 하면 더 안 건드린다. */}
                        <Btn disabled={busy || l.revision === 0} onClick={() => toggleSeal(l.id, !!l.sealed_at)}>
                          {l.sealed_at ? '봉인 해제' : '완료 처리'}
                        </Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          );
        })}

        <Card>
          <CardTitle>최근 배정</CardTitle>
          <CardNote>일주일치. 봇이 아침에 받은 몫과 끝냈는지 여부입니다.</CardNote>
          {recentAssignments.length === 0 ? (
            <div style={{ color: '#64748b', fontSize: '0.85rem' }}>아직 배정이 없습니다.</div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>날짜</th>
                  <th style={{ width: 150 }}>봇</th>
                  <th>차시</th>
                  <th style={{ width: 70 }}>종류</th>
                  <th style={{ width: 70 }}>상태</th>
                </tr>
              </thead>
              <tbody>
                {recentAssignments.map((a, i) => (
                  <tr key={`${a.assign_date}-${a.lesson_id}-${i}`}>
                    <td style={{ color: '#64748b' }}>{String(a.assign_date).slice(0, 10)}</td>
                    <td>{a.agent_name}</td>
                    <td>
                      <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>{a.course_title} · </span>
                      {a.lesson_title}
                    </td>
                    <td>{a.mode === 'deepen' ? '보완' : '신규'}</td>
                    <td>
                      <Tag $tone={a.status === 'done' ? 'self' : 'human'}>
                        {a.status === 'done' ? '완료' : '진행'}
                      </Tag>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </Inner>
    </Page>
  );
}
